/**
 * v-reveal — fade + rise an element in the first time it scrolls into view.
 *
 * Add `v-reveal` to any element (optionally `v-reveal="{ delay: 80 }"`). It
 * gives the long data pages a sense of coming to life as you explore, without
 * a heavy animation library. Elements already on screen at load reveal on the
 * next frame, so there's no flash of hidden content.
 *
 * Respects prefers-reduced-motion: those users see everything immediately with
 * no transform. Import and expose it in a component's <script setup> as
 * `vReveal` and Vue registers it as `v-reveal` locally.
 */
import type { Directive } from 'vue';

const prefersReduced =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

interface RevealOpts { delay?: number }

export const vReveal: Directive<HTMLElement, RevealOpts | undefined> = {
  mounted(el, binding) {
    if (prefersReduced) return;
    const delay = binding.value?.delay ?? 0;
    el.style.opacity = '0';
    el.style.transform = 'translateY(14px)';
    el.style.transition =
      `opacity 0.5s ease ${delay}ms, transform 0.55s cubic-bezier(0.22, 0.61, 0.36, 1) ${delay}ms`;
    el.style.willChange = 'opacity, transform';

    const io = new IntersectionObserver((entries, obs) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        el.style.opacity = '1';
        el.style.transform = 'none';
        obs.unobserve(el);
        // Once revealed, strip the inline reveal styles so they don't linger
        // and fight the element's own hover/open transitions.
        const cleanup = () => {
          el.style.transition = '';
          el.style.transform = '';
          el.style.willChange = '';
          el.style.opacity = '';
          el.removeEventListener('transitionend', cleanup);
        };
        el.addEventListener('transitionend', cleanup);
      }
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });

    io.observe(el);
    (el as HTMLElement & { _revealIO?: IntersectionObserver })._revealIO = io;
  },
  unmounted(el) {
    (el as HTMLElement & { _revealIO?: IntersectionObserver })._revealIO?.disconnect();
  },
};
